import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { fetchNamedVersion } from "reducers/CreativeVersions";
import { useTooltipRebuild } from "hooks/useTooltipRebuild";
import useFetchFallback from "hooks/useFetchFallback";

import {
	API_URL,
	D_TREE_URL,
	DECISIONING_TYPES,
	AUTO_OPTIMIZATION_LABELS
} from "utils/constants";

export function DecisioningEditLink({ children }){
	return <Link to={`modal/multi-variant/edit/`}>{children}</Link>;
}

const decisioningHeaderByType = {
	[DECISIONING_TYPES.DECISION_TREE]: ({ creative: { id, decisioningName, decisionTreeId, dynamicTarget } }) => {
		if(dynamicTarget === "D-tree (Legacy)"){
			return (
				<span
					className = "name-overflow"
					data-tip = "Legacy D-Trees can't be edited"
				>
					{decisioningName}
				</span>
			);
		}
		return (
			<a
				href={`${D_TREE_URL}/v/edit/${decisionTreeId}?creative=${id}`}
				target="_blank"
				className="name-overflow"
				title={decisioningName}
			>
				{decisioningName}
			</a>
		);
	},
	[DECISIONING_TYPES.DECISION_SHEET]: ({ libraryId, creative: { id, decisioningName } }) => {
		return (
			<a
				download
				href={`${API_URL}/crm/v1/creative-libraries/${libraryId}/creatives/${id}/hedges/export`}
				target="_blank"
				className="name-overflow"
				title={decisioningName}
			>
				{decisioningName}
			</a>
		);
	},
	[DECISIONING_TYPES.WEIGHTED_ROTATION]: () => {
		return (
			<DecisioningEditLink>
				Weighted Rotation
			</DecisioningEditLink>
		);
	},
	[DECISIONING_TYPES.AUTO_OPTIMIZATION]: ({ creative: { decisioningName } }) => {
		return (
			<DecisioningEditLink>
				{AUTO_OPTIMIZATION_LABELS[decisioningName]}
			</DecisioningEditLink>
		);
	},
	[DECISIONING_TYPES.PUBLISHED_VERSION]: ({ libraryId, creative }) => {
		const { id: creativeId, decisioningName } = creative;
		const dispatch = useDispatch();
		const isVersionsLoaded = useSelector(state => state.creativeVersions.isLoaded);

		const version = useFetchFallback({
			selector: state => state.creativeVersions.items.find(version => (version.name === decisioningName)),
			fetcher: () => dispatch(fetchNamedVersion(libraryId, creativeId, decisioningName)),
			shouldFetch: isVersionsLoaded,
		});

		let $name = <span className="name-overflow">{decisioningName}</span>;

		if(version){
			return (
				<Link to={`${version.id}/modal/preview-version/`}>
					{$name}
				</Link>
			);
		}
		return $name;
	},
};

export default function DecisioningHeader({
	libraryId,
	creative,
}){
	let { decisioningType } = creative;
	let Formatter = decisioningHeaderByType[decisioningType];

	useTooltipRebuild(decisioningType);

	return Formatter ? <Formatter libraryId={libraryId} creative={creative} /> : "--";
}

DecisioningHeader.displayName = "DecisioningHeader";
